import React, { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Box, IconButton } from '@mui/material'
import { Moon, Sun } from 'lucide-react'

import { useColorMode } from 'hooks'

import { SignInBackground } from 'pages/onboarding/SignInPage/SignInBackground'
import { authCornerControlSx } from 'pages/onboarding/components/AuthBackButton'
import { authPx } from 'pages/onboarding/components/authScale'

import { AmbientSurgeProvider } from './ambientSurge'
import { isPreAuthPath, rememberPreAuthColorMode } from 'utils/colorModeHandoff'

/**
 * Remembers, for THIS tab, the colour mode a pre-auth page is showing, so the next page
 * load of a pre-auth route opens in the same mode. That load is what "Log in with social"
 * produces: it leaves for the API / WorkOS and comes back to `/auth/callback`, where the
 * second-factor challenge renders. Without this, the hand-off param had been stripped and the
 * stored preference is ignored there, so the verify screen showed the OS mode instead of the
 * sign-in page's. See `PRE_AUTH_MODE_KEY` in utils/colorModeHandoff.
 *
 * Only on pre-auth paths: this layout also wraps one signed-in onboarding route (VRG-646),
 * and the signed-in user's theme must not be written into the anonymous pre-auth slot.
 */
const PreAuthColorModeRecorder = () => {
  const { mode } = useColorMode()
  const { pathname } = useLocation()
  useEffect(() => {
    if (isPreAuthPath(pathname)) rememberPreAuthColorMode(mode)
  }, [mode, pathname])
  return null
}

/**
 * The light/dark control, top-right on every auth screen (2026-09-23).
 *
 * Spencer asked whether the website's choice carries over or whether this
 * page needs its own. Both: vera.inc's Log in link now appends ?theme=<mode>,
 * which `utils/colorModeHandoff` (VRG-600) applies and strips on arrival, so a
 * visitor lands in the mode they chose there. This control is for everyone
 * else — a bookmark, a typed URL, an invite email — and matches vera.inc
 * /enter, which already has one in the same corner. It writes the same
 * `vera-color-mode` preference the app itself uses, so the choice follows the
 * user inside after sign-in.
 */
const AuthThemeToggle = () => {
  const { resolvedMode, toggle } = useColorMode()
  const next = resolvedMode === 'dark' ? 'light' : 'dark'
  return (
    <IconButton
      onClick={toggle}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      // Colour, hover plate, 36px box, 8px radius, fixed corner, icon size,
      // the desktop scale and zIndex 20 all come from `authCornerControlSx`,
      // shared with the auth Back button (top-left) so the two corners cannot
      // drift. The ⚠️ on why the zIndex is 20 (sign-up's fixed z-index 10
      // shell) moved there with it. `p: 0` so the square is the box, not
      // IconButton's 8px padding round an 18px icon.
      sx={(theme) => ({ ...authCornerControlSx(theme, 'right'), width: authPx(36), p: 0 })}
    >
      {resolvedMode === 'dark' ? <Moon size={18} strokeWidth={1.75} /> : <Sun size={18} strokeWidth={1.75} />}
    </IconButton>
  )
}

/**
 * The shared ambient shell for every auth screen: one full-viewport backdrop,
 * rendered ONCE and reused across sign-in, sign-up, password reset and email
 * verification.
 *
 * WHY IT EXISTS — the blob flash (VERA-1095). Sign-in and sign-up are sibling
 * routes, so navigating between them destroyed the background and built a new
 * one. Seeding the animations with a negative delay fixed the PHASE (the blobs
 * no longer jumped back to their starting positions) but could not fix the
 * REPAINT: the bottom blob is an 1800px element under `filter: blur(250px)`,
 * and inserting that fresh forces the compositor to rasterise a very expensive
 * layer from scratch. For the frame or two that takes, there is nothing to
 * composite — which is the flash, and it landed in the middle of the
 * cross-fade, exactly where the eye already was.
 *
 * No amount of timing correction removes a repaint. The node has to survive the
 * navigation, and a layout route is what makes it survive: React Router keeps
 * the layout element mounted while only the child route swaps, so the blur
 * layer is rasterised once for the whole auth session.
 *
 * WHAT THIS COST, and why it was worth paying now. Each auth page used to own
 * an OPAQUE `#000000` shell, which would paint straight over a hoisted
 * backdrop. All four had to become transparent for this to work — the exact
 * change the light/dark theme work needs anyway, since those four literals are
 * what pin auth to dark. Doing them together means the shells get exactly one
 * owner: this file. That is also why the background colour lives HERE and is
 * theme-aware, rather than being repeated four times.
 *
 * The pages keep their own `position: fixed` shells for LAYOUT (centring,
 * scrolling, per-page overflow rules). They just no longer paint a background.
 *
 * NOT `LayoutAuth`, deliberately. That one renders an empty `position: fixed`
 * header at zIndex 100 with an opaque background, which painted over the
 * sign-up wordmark when it was tried there, and pins its outlet to 440px, which
 * the 3-card signup layout escapes anyway. This layout adds a backdrop and
 * nothing else.
 */
export const LayoutAuthAmbient = () => (
  /* The provider wraps BOTH the backdrop and the `<Outlet />` — the only point
     in the tree where a page and the background it wants to affect are both in
     scope. See `ambientSurge.tsx`. Routes that never call `setSurge` are
     unaffected: the default is 0, which is the behaviour that existed before. */
  <AmbientSurgeProvider>
    <PreAuthColorModeRecorder />
    {/* The backdrop. `zIndex: 0` and first in DOM order, so every page shell
        rendered by the Outlet composites above it without needing to know it
        exists. `overflow: hidden` clips the orbiting blobs, which is what stops
        them generating phantom scroll at their extremes. */}
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        // Theme-aware, and the ONLY place the auth backdrop colour is set.
        // Was a literal `#000000` repeated in four page files; the comment
        // there justified the literal on the grounds that sign-in and sign-up
        // navigate straight into each other, so a base colour differing by even
        // one step reads as a flash at the swap. Hoisting makes that argument
        // moot in the strongest way: there is now exactly one element, so the
        // two pages cannot disagree about its colour.
        bgcolor: 'background.default',
      }}
    >
      <SignInBackground />
    </Box>
    <Outlet />
    <AuthThemeToggle />
  </AmbientSurgeProvider>
)

export default LayoutAuthAmbient
